import React from "react";
//#region types
import { CurrencyCode } from "models/enums/currency";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { Reconciliation, ReconciliationWidgetProps } from "models/propTypes/accountingPropTypes";
import { DateConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { Skeletons, TableAsync } from "components/common";
import ReconciliationStatus from "../../reconciliation/reconciliationStatus";
import { useLocation, useNavigate } from "react-router-dom";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { UrlHelper, LocalizationHelperInstance } from "utils/helpers";

const ReconciliationWidgetListView: React.FC<ReconciliationWidgetProps> = (props) => {
  const financialReportService = useFinancialReportService();
  const [result, setResult] = React.useState<Reconciliation[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [startDate, setStartDate] = React.useState<string>("iB");
  const [endDate, setEndDate] = React.useState<string>("uB");
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    setLoading(true);
    financialReportService
      .getWidgetReconciliationReport(props.reconciliationDate!)
      .then((res) => {
        setResult(
          res.rows
            ? res.rows!.map((x) => ({
                id: x.id!,
                name: x.description!,
                ib: x.columns![0].iB,
                changePercentage: x.columns![0].differencePercentage,
                ub: x.columns![0].uB,
                checked: x.columns![0].checked,
              }))
            : []
        );
        setStartDate(
          res.startDate
            ? LocalizationHelperInstance.getFormattedDate(res.startDate, DateConstants.DateMonthYearShortConfig)
            : "iB"
        );
        setEndDate(
          res.endDate
            ? LocalizationHelperInstance.getFormattedDate(res.endDate, DateConstants.DateMonthYearShortConfig)
            : "uB"
        );
      })
      .finally(() => setLoading(false));
  }, [props.reconciliationDate]);

  const onRowClick = (row: Reconciliation) => {
    navigate(UrlHelper.formatPath(`/AdvisoryView/ReconciliationReport#${row.id}`), {
      state: { background: location },
    });
  };

  return loading ? (
    <Skeletons.TableSkeleton />
  ) : (
    <Grid container direction={"column"} spacing={3} className={cssExports.reconciliationWidget}>
      <Grid item>
        <TableAsync<Reconciliation>
          tableId="AdvisorReconciliationWidget"
          properties={{
            name: {
              description: TranslationKeys.Common.Name,
            },
            ib: {
              description: startDate,
              formatter: (reconciliation: Reconciliation) =>
                new Amount(CurrencyCode.SEK, reconciliation.ib.toFixed(0) as unknown as number),
              alignment: "right",
              noWrap: true,
              classification: "amount",
            },
            changePercentage: {
              description: "%",
              formatter: (reconciliation: Reconciliation) =>
                `${reconciliation.changePercentage > 0 ? "+" : ""}${(reconciliation.changePercentage * 100).toFixed(
                  0
                )} %`,
              alignment: "right",
              noWrap: true,
              classification: "number",
            },
            ub: {
              description: endDate,
              formatter: (reconciliation: Reconciliation) =>
                new Amount(CurrencyCode.SEK, reconciliation.ub.toFixed(0) as unknown as number),
              alignment: "right",
              noWrap: true,
              classification: "amount",
            },
            checked: {
              description: TranslationKeys.Common.Status,
              formatter: (reconciliation: Reconciliation) => (
                <ReconciliationStatus isChecked={reconciliation.checked ?? false} />
              ),
              alignment: "center",
            },
          }}
          filter={result}
          pagination={false}
          disableSort
          rowClickHandler={onRowClick}
        />
      </Grid>
    </Grid>
  );
};

export default ReconciliationWidgetListView;
